import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const HouseKeepingSetup = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [roomToUnassign, setRoomToUnassign] = useState(null);
  const [assignData, setAssignData] = useState({
    room_id: '',
    staff_name: ''
  });
  const [successMessage, setSuccessMessage] = useState('');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchStaff(), fetchAssignments(), fetchRooms()]);
    setLoading(false);
  };

  const fetchStaff = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/setup/staff');
      if (data.success) setStaff(data.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/setup/assignments');
      if (data.success) setAssignments(data.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      const data = await apiGet('/admin/housekeeping/operations/status');
      if (data.success) setRooms(data.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleAssignStaff = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/housekeeping/setup/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignData)
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Staff assigned successfully!');
        setShowAssignModal(false);
        fetchAllData();
        setAssignData({ room_id: '', staff_name: '' });
      }
    } catch (error) {
      console.error('Error assigning staff:', error);
    }
  };

  const handleUnassignStaff = async () => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/housekeeping/setup/unassign/${roomToUnassign.room_id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Staff unassigned successfully!');
        setShowConfirmModal(false);
        setRoomToUnassign(null);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error unassigning staff:', error);
    }
  };

  const confirmUnassign = (room) => {
    setRoomToUnassign(room);
    setShowConfirmModal(true);
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : isTablet ? '24px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px 16px 4px' : '0 0 24px 0'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      justifyContent: 'space-between'
    },
    button: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '6px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: colors.primary,
      color: colors.white
    },
    dangerButton: {
      background: colors.danger,
      color: colors.white,
      padding: isMobile ? '6px 10px' : '6px 12px',
      fontSize: isMobile ? '11px' : '12px'
    },
    staffGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '16px'
    },
    staffCard: {
      padding: isMobile ? '16px' : '20px',
      background: colors.lightGray,
      borderRadius: '10px',
      border: `2px solid ${colors.mediumGray}`,
      transition: 'all 0.3s ease'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '20px' : '40px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: isMobile ? '100%' : '500px',
      width: '100%'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: '13px',
      fontFamily: 'inherit',
      background: colors.white
    },
    successMessage: {
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '10px',
      marginBottom: '16px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      background: 'rgba(74, 124, 42, 0.15)',
      color: colors.success,
      border: `2px solid ${colors.success}`,
      margin: isMobile ? '0 4px 12px 4px' : '0 0 16px 0'
    },
    actionButton: {
      padding: '6px 12px',
      borderRadius: '6px',
      border: 'none',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading setup data...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={styles.successMessage}>{successMessage}</div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Housekeeping Setup</h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Staff & categories' : 'Configure housekeeping staff, room categories, and cleaning schedules'}
        </p>
      </div>

      <div style={styles.contentCard}>
        <div style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>👥</span>
            <span>Housekeeping Staff</span>
          </div>
          <button onClick={() => setShowAssignModal(true)} style={styles.button}>
            + Assign Staff
          </button>
        </div>

        {staff.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No staff members assigned yet
          </div>
        ) : (
          <div style={styles.staffGrid}>
            {staff.map((member, index) => (
              <div key={index} style={styles.staffCard}>
                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', marginBottom: '8px', color: colors.primary }}>
                  {member.name}
                </div>
                <div style={{ fontSize: '13px', color: colors.textLight }}>
                  Rooms Cleaned: <strong>{member.rooms_cleaned}</strong>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📋</span>
            <span>Room Assignments</span>
          </div>
        </h2>

        {assignments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No room assignments yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {assignments.map((assignment, index) => (
              <div
                key={index}
                style={{
                  padding: isMobile ? '14px' : '16px',
                  background: colors.lightGray,
                  borderRadius: '8px',
                  border: `1px solid ${colors.mediumGray}`
                }}
              >
                <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', marginBottom: '8px', color: colors.textDark }}>
                  {assignment.cleaned_by}
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px' }}>
                  Assigned Rooms: <strong>{assignment.room_count}</strong>
                </div>
                <div style={{ fontSize: '12px', color: colors.textLight }}>
                  Rooms: {assignment.assigned_rooms}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.contentCard}>
        <h2 style={styles.sectionTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🏨</span>
            <span>All Rooms</span>
          </div>
        </h2>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px' }}>
            <thead>
              <tr>
                <th style={{ background: colors.lightGray, padding: isMobile ? '10px' : '12px', textAlign: 'left', fontWeight: '700', borderBottom: `2px solid ${colors.mediumGray}` }}>
                  Room
                </th>
                <th style={{ background: colors.lightGray, padding: isMobile ? '10px' : '12px', textAlign: 'left', fontWeight: '700', borderBottom: `2px solid ${colors.mediumGray}` }}>
                  Type
                </th>
                {!isMobile && (
                  <th style={{ background: colors.lightGray, padding: '12px', textAlign: 'left', fontWeight: '700', borderBottom: `2px solid ${colors.mediumGray}` }}>
                    Floor
                  </th>
                )}
                <th style={{ background: colors.lightGray, padding: isMobile ? '10px' : '12px', textAlign: 'left', fontWeight: '700', borderBottom: `2px solid ${colors.mediumGray}` }}>
                  Assigned To
                </th>
                <th style={{ background: colors.lightGray, padding: isMobile ? '10px' : '12px', textAlign: 'center', fontWeight: '700', borderBottom: `2px solid ${colors.mediumGray}` }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rooms.map(room => (
                <tr key={room.id}>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                    <strong>{room.room_number}</strong>
                  </td>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                    {room.room_type}
                  </td>
                  {!isMobile && (
                    <td style={{ padding: '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                      Floor {room.floor}
                    </td>
                  )}
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}` }}>
                    {room.cleaned_by || <span style={{ color: colors.textLight }}>Unassigned</span>}
                  </td>
                  <td style={{ padding: isMobile ? '10px' : '12px', borderBottom: `1px solid ${colors.lightGray}`, textAlign: 'center' }}>
                    {room.cleaned_by ? (
                      <button
                        onClick={() => confirmUnassign(room)}
                        style={{
                          ...styles.actionButton,
                          ...styles.dangerButton
                        }}
                      >
                        🗑️ Unassign
                      </button>
                    ) : (
                      <span style={{ color: colors.textLight, fontSize: '11px' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Staff Modal */}
      {showAssignModal && (
        <div style={styles.modal} onClick={() => setShowAssignModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '20px', fontWeight: '700' }}>
              Assign Staff to Room
            </h3>

            <form onSubmit={handleAssignStaff}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select Room *</label>
                <select
                  value={assignData.room_id}
                  onChange={(e) => setAssignData({ ...assignData, room_id: e.target.value })}
                  style={styles.select}
                  required
                >
                  <option value="">Choose a room...</option>
                  {rooms.map(room => (
                    <option key={room.room_id} value={room.room_id}>
                      {room.room_number} - {room.room_type} (Floor {room.floor})
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Staff Name *</label>
                <input
                  type="text"
                  value={assignData.staff_name}
                  onChange={(e) => setAssignData({ ...assignData, staff_name: e.target.value })}
                  style={styles.input}
                  placeholder="Enter staff name"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="submit" style={{ ...styles.button, flex: 1 }}>
                  Assign Staff
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  style={{ ...styles.button, background: colors.mediumGray, color: colors.textDark, flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Unassign Modal */}
      {showConfirmModal && roomToUnassign && (
        <div style={styles.modal} onClick={() => setShowConfirmModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontSize: isMobile ? '18px' : '22px', marginBottom: '16px', fontWeight: '700', color: colors.danger }}>
              ⚠️ Confirm Unassign
            </h3>

            <p style={{ fontSize: '14px', marginBottom: '20px', color: colors.textDark }}>
              Are you sure you want to unassign <strong>{roomToUnassign.cleaned_by}</strong> from Room <strong>{roomToUnassign.room_number}</strong>?
            </p>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleUnassignStaff}
                style={{
                  ...styles.button,
                  background: colors.danger,
                  flex: 1
                }}
              >
                Yes, Unassign
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setRoomToUnassign(null);
                }}
                style={{
                  ...styles.button,
                  background: colors.mediumGray,
                  color: colors.textDark,
                  flex: 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default HouseKeepingSetup;